import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Button } from "react-bootstrap";
import "chart.js/auto";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  CREDIT_TYPE_LABEL,
  CREDIT_STATUS_LABEL,
  PARTNER_TYPE_LABEL,
  PartnerType,
  fetchCreditNotesReport,
  refreshCreditNotesReport,
} from "src/store/reducers/creditNotes";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";

const monthName = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const CreditNotesReport = () => {
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState(false);

  const reportStatus = useAppSelector(
    (state) => state.creditNotes.report.status
  );
  const reportData = useAppSelector(
    (state) => state.creditNotes.report.data
  );

  const {
    handleChange,
    values,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      from_date: "",
      to_date: "",
      credit_type: "",
      status: "",
      group_by: "month",
    },
    validationSchema: Yup.object({}),
    enableReinitialize: true,
    onSubmit: async (vals) => {
      setPending(true);
      const payload: Record<string, any> = {};
      if (vals.from_date) payload.from_date = vals.from_date;
      if (vals.to_date) payload.to_date = vals.to_date;
      if (vals.credit_type) payload.credit_type = Number(vals.credit_type);
      if (vals.status) payload.status = Number(vals.status);
      if (vals.group_by) payload.group_by = vals.group_by;
      try {
        await dispatch(fetchCreditNotesReport(payload)).unwrap();
        toast.success("Report updated");
      } catch (e: any) {
        toast.error(e.message || "Failed to load report");
      } finally {
        setPending(false);
      }
    },
  });

  const retry = useCallback(() => {
    dispatch(fetchCreditNotesReport({ group_by: "month" }));
  }, [dispatch]);

  useEffect(() => {
    if (reportStatus === "idle") retry();
  }, [reportStatus, retry]);

  useEffect(() => {
    return () => {
      dispatch(refreshCreditNotesReport());
    };
  }, [dispatch]);

  // Bar chart — only meaningful for group_by=month
  const chartData = useMemo(() => {
    if (values.group_by !== "month" || !reportData?.length) {
      return null;
    }
    const labels = reportData.map((d) =>
      d.month ? monthName[new Date(d.month).getMonth()] : ""
    );
    const given = reportData.filter((d) => d.credit_type === 1);
    const received = reportData.filter((d) => d.credit_type === 2);

    // Build label set from all entries
    const allLabels = Array.from(
      new Set(
        reportData.map((d) =>
          d.month ? monthName[new Date(d.month).getMonth()] : ""
        )
      )
    );

    const givenRemaining = allLabels.map((lbl) => {
      const entry = given.find(
        (d) => d.month && monthName[new Date(d.month).getMonth()] === lbl
      );
      return entry ? parseFloat(entry.total_remaining || "0") : 0;
    });

    const receivedRemaining = allLabels.map((lbl) => {
      const entry = received.find(
        (d) => d.month && monthName[new Date(d.month).getMonth()] === lbl
      );
      return entry ? parseFloat(entry.total_remaining || "0") : 0;
    });

    const maxVal = Math.max(...givenRemaining, ...receivedRemaining, 1);

    return {
      data: {
        defaultFontFamily: "Poppins",
        labels: allLabels,
        datasets: [
          {
            label: "Credit Given (Remaining)",
            data: givenRemaining,
            backgroundColor: "#34348f",
            pointBackgroundColor: "#34348f",
            tension: 0.4,
            categoryPercentage: 0.7,
          },
          {
            label: "Credit Received (Remaining)",
            data: receivedRemaining,
            backgroundColor: "#3AC977",
            pointBackgroundColor: "#3AC977",
            tension: 0.4,
            categoryPercentage: 0.7,
          },
        ],
      },
      options: {
        plugins: { legend: { display: true } },
        scales: {
          y: {
            min: 0,
            max: maxVal,
            ticks: { beginAtZero: true, padding: 0 },
            grid: { color: "rgba(255,255,255,0.1)" },
          },
          x: {
            ticks: { padding: 0 },
            grid: { color: "rgba(255,255,255,0.1)" },
          },
        },
      },
    };
  }, [reportData, values.group_by]);

  const renderTable = () => {
    if (!reportData || reportData.length === 0) return null;

    switch (values.group_by) {
      case "month":
        return (
          <table className="table table-hover dataTable no-footer mb-0">
            <thead>
              <tr>
                <th>Month</th>
                <th>Credit Type</th>
                <th>Count</th>
                <th>Total Amount</th>
                <th>Total Used</th>
                <th>Total Remaining</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, i) => (
                <tr key={i}>
                  <td>
                    {row.month
                      ? new Date(row.month).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "short",
                        })
                      : "—"}
                  </td>
                  <td>
                    {row.credit_type
                      ? CREDIT_TYPE_LABEL[row.credit_type]
                      : "—"}
                  </td>
                  <td>{row.count ?? "—"}</td>
                  <td>
                    {row.total_amount
                      ? parseFloat(row.total_amount).toLocaleString("en-IN")
                      : "—"}
                  </td>
                  <td>
                    {row.total_used
                      ? parseFloat(row.total_used).toLocaleString("en-IN")
                      : "—"}
                  </td>
                  <td>
                    <strong>
                      {row.total_remaining
                        ? parseFloat(row.total_remaining).toLocaleString(
                            "en-IN"
                          )
                        : "—"}
                    </strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case "user":
        return (
          <table className="table table-hover dataTable no-footer mb-0">
            <thead>
              <tr>
                <th>User</th>
                <th>Credit Type</th>
                <th>Count</th>
                <th>Total Amount</th>
                <th>Total Used</th>
                <th>Total Remaining</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, i) => (
                <tr key={i}>
                  <td>{row.user__name || "—"}</td>
                  <td>
                    {row.credit_type
                      ? CREDIT_TYPE_LABEL[row.credit_type]
                      : "—"}
                  </td>
                  <td>{row.count ?? "—"}</td>
                  <td>
                    {row.total_amount
                      ? parseFloat(row.total_amount).toLocaleString("en-IN")
                      : "—"}
                  </td>
                  <td>
                    {row.total_used
                      ? parseFloat(row.total_used).toLocaleString("en-IN")
                      : "—"}
                  </td>
                  <td>
                    <strong>
                      {row.total_remaining
                        ? parseFloat(row.total_remaining).toLocaleString(
                            "en-IN"
                          )
                        : "—"}
                    </strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case "partner":
        return (
          <table className="table table-hover dataTable no-footer mb-0">
            <thead>
              <tr>
                <th>Partner Type</th>
                <th>Count</th>
                <th>Total Amount</th>
                <th>Total Used</th>
                <th>Total Remaining</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, i) => (
                <tr key={i}>
                  <td>
                    {row.partner_type
                      ? PARTNER_TYPE_LABEL[row.partner_type as PartnerType] ||
                        row.partner_type
                      : "—"}
                  </td>
                  <td>{row.count ?? "—"}</td>
                  <td>
                    {row.total_amount
                      ? parseFloat(row.total_amount).toLocaleString("en-IN")
                      : "—"}
                  </td>
                  <td>
                    {row.total_used
                      ? parseFloat(row.total_used).toLocaleString("en-IN")
                      : "—"}
                  </td>
                  <td>
                    <strong>
                      {row.total_remaining
                        ? parseFloat(row.total_remaining).toLocaleString(
                            "en-IN"
                          )
                        : "—"}
                    </strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Filter */}
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12">
            <div className="card h-auto">
              <div className="card-header">
                <h4 className="heading mb-0">Credit Notes Report</h4>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="row container-fluid">
                  <div className="col-xl-3 mb-3">
                    <CustomDatePickerWithLabel
                      id="from_date"
                      placeholder="From Date"
                      pickerType="YYYY-MM-DD"
                      handleChange={(d) => setFieldValue("from_date", d)}
                      value={values.from_date}
                      selectsStart
                      endDate={values.to_date}
                    />
                  </div>
                  <div className="col-xl-3 mb-3">
                    <CustomDatePickerWithLabel
                      id="to_date"
                      placeholder="To Date"
                      pickerType="YYYY-MM-DD"
                      handleChange={(d) => setFieldValue("to_date", d)}
                      value={values.to_date}
                      selectsEnd
                      startDate={values.from_date}
                    />
                  </div>
                  <div className="col-xl-2 mb-3">
                    <CustomSelectWithLabel
                      id="credit_type"
                      handleChange={handleChange}
                      value={values.credit_type}
                    >
                      <option value="">All Types</option>
                      <option value="1">{CREDIT_TYPE_LABEL[1]}</option>
                      <option value="2">{CREDIT_TYPE_LABEL[2]}</option>
                    </CustomSelectWithLabel>
                  </div>
                  <div className="col-xl-2 mb-3">
                    <CustomSelectWithLabel
                      id="status"
                      handleChange={handleChange}
                      value={values.status}
                    >
                      <option value="">All Statuses</option>
                      {Object.entries(CREDIT_STATUS_LABEL).map(
                        ([val, label]) => (
                          <option value={val} key={val}>
                            {label}
                          </option>
                        )
                      )}
                    </CustomSelectWithLabel>
                  </div>
                  <div className="col-xl-2 mb-3">
                    <CustomSelectWithLabel
                      id="group_by"
                      handleChange={handleChange}
                      value={values.group_by}
                    >
                      <option value="month">Group by Month</option>
                      <option value="user">Group by User</option>
                      <option value="partner">Group by Partner</option>
                    </CustomSelectWithLabel>
                  </div>
                  <div className="col-xl-2 mb-3">
                    <Button
                      className="form-control"
                      disabled={pending}
                      type="submit"
                    >
                      {pending ? "Loading" : "Submit"}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Chart — only for group_by=month */}
      {chartData && (
        <div className="container-fluid">
          <div className="row">
            <div className="col-xl-12">
              <div className="card h-auto">
                <div className="card-header">
                  <h4 className="heading mb-0">Remaining Credit by Month</h4>
                </div>
                <div className="card-body">
                  <Bar
                    data={chartData.data}
                    options={chartData.options}
                    height={150}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12">
            <div className="card h-auto">
              <div className="card-header">
                <h4 className="heading mb-0">
                  Results{" "}
                  {values.group_by
                    ? `— grouped by ${values.group_by}`
                    : ""}
                </h4>
              </div>
              <div className="card-body">
                {reportStatus === "success" ? (
                  reportData && reportData.length > 0 ? (
                    <div className="table-responsive active-projects">
                      <div className="dataTables_wrapper no-footer">
                        {renderTable()}
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <strong>No data for selected filters</strong>
                    </div>
                  )
                ) : reportStatus === "pending" ? (
                  <div className="d-flex align-items-center">
                    <strong>Loading...</strong>
                    <div
                      className="spinner-border ms-auto"
                      role="status"
                      aria-hidden="true"
                    ></div>
                  </div>
                ) : reportStatus === "error" ? (
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={retry}
                    >
                      Retry
                    </button>
                    &nbsp;
                    <strong>Please try again</strong>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreditNotesReport;
